import { Suspense, lazy, useCallback, useEffect, useMemo, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { apiReportVitals } from "../../api/client";
import {
  canAccessRoute,
  findPageByPath,
  getVisibleDomains,
  getVisiblePages,
  HR_PORTAL_PAGE,
  loadActiveDomain,
  resolveNavMode,
  saveActiveDomain,
} from "../../config/roleMenus";
import { useIsMobile } from "../../hooks/useIsMobile";
import { usePendingApprovalCount } from "../../hooks/usePendingApprovalCount";
import { useAuth } from "../../store/AuthContext";
import { initErrorMonitor } from "../../utils/errorMonitor";
import { safeGetItem } from "../../utils/safe-storage";
import { initWebVitals } from "../../utils/webVitals";
import ErrorBoundary from "../ErrorBoundary";

// 全局浮层改为懒加载：AI 助手带 markdown 渲染栈（react-markdown / remark / micromark 等约 190KB），
// 演示导览与新手引导也不属于首屏必需 —— 静态引入会把它们全部塞进入口 chunk（实测入口因此多背 190KB）。
// 三者都是「用户点了才展开」的浮层，首屏只需要一个按钮，故按需加载。
const AIAssistant = lazy(() => import("../ai/AIAssistant"));
const DemoGuide = lazy(() => import("../DemoGuide"));
const OnboardingGuide = lazy(() => import("../OnboardingGuide"));
import { Button } from "../ui/button";
import { Drawer } from "../ui/drawer";
import GlobalSearch from "./GlobalSearch";
import MobileNavPanel from "./MobileNavPanel";
import MobileTabBar from "./MobileTabBar";
import TopNav from "./TopNav";
import { useSessionExpiryModal } from "./SessionExpiryModal";
import "../../styles/components/layout.css";

/**
 * 应用壳（2026-09-24 导航形态调整后）。
 *
 * 形态由「当前角色可见页面数」决定（resolveNavMode）：
 *   · **tabs**（≤ NAV_MODE_THRESHOLD）：顶部平铺页面页签，无二级导航
 *     —— 与参考工程 hr_dynimic_mgmt 的纯顶部形态一致；窄角色（质检/仓管/采购等）只需一次点击。
 *   · **domains**（> 阈值）：顶部列业务域页签，**悬停/点击/键盘展开二级浮层**选择域内页面；
 *     已不再渲染左侧栏（各域只有 1~6 个页面，常驻侧栏占宽且浪费），域切换仍记忆在 localStorage，
 *     刷新/切页后停在同一个域。
 *
 * 保持不变的三件事：
 *   · 导航唯一来源是 config/roleMenus（按角色过滤；空角色不显示导航）；
 *   · 无权限时渲染 403 而不是静默跳转（保留 URL，便于确认在哪儿被拦住）；
 *     注意「未迁移（ready:false）」不算无权限 —— 深链仍走「建设中」占位页；
 *   · 页面级异常由 ErrorBoundary 兜底并上报。
 * 已移除：面包屑（`PageBreadcrumb`）、已访问页签（`PageTabs`）、顶栏标题/路径 ——
 * 位置信息由页签高亮与侧栏承担，此前同一页面名一处重复出现三次。
 */
export default function AppShell() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { modal: sessionExpiryModal } = useSessionExpiryModal();

  const roleNames = user?.roleNames ?? [];
  const roleKey = roleNames.join(",");

  const visiblePages = useMemo(() => getVisiblePages(roleNames), [roleKey]);
  const visibleDomains = useMemo(() => getVisibleDomains(roleNames), [roleKey]);
  const mode = resolveNavMode(visiblePages.length);
  const activeKey = useMemo(() => findPageByPath(location.pathname)?.key ?? location.pathname, [location.pathname]);

  // 说明：侧栏（及其折叠态 v2_sider_collapsed）已于 2026-09-24 移除，二级导航改由顶栏浮层承担

  const [domainKey, setDomainKey] = useState<string | null>(() => loadActiveDomain());
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // 当前业务域：优先按路径归属，其次按记忆，最后取第一个可见域
  const activeDomain = useMemo(() => {
    const owner = visibleDomains.find((domain) => domain.pages.some((page) => page.key === activeKey));
    if (owner) return owner;
    return visibleDomains.find((domain) => domain.key === domainKey) ?? visibleDomains[0] ?? null;
  }, [visibleDomains, activeKey, domainKey]);

  // 「待我审批」：顶栏角标与驾驶舱 KPI 卡共用同一份计数（见 usePendingApprovalCount），
  // 避免常驻壳层与驾驶舱各发一份 60s 轮询。仅对拥有审批页访问权的角色请求。
  const isApprover = canAccessRoute(roleNames, "/approval");
  const { count: pendingApprovals, refresh: refreshPendingApprovals } = usePendingApprovalCount(isApprover);

  // 审批决定后角标要立刻下降：路由变化即刷新（hook 内有 2s 最小间隔，连续跳页不会打爆接口）
  useEffect(() => {
    refreshPendingApprovals();
  }, [location.pathname, refreshPendingApprovals]);

  // 角标挂点：domains 形态挂在「含审批工作台的域」页签上；tabs 形态没有域页签，直接挂页面
  const domainBadges = useMemo(() => {
    if (!isApprover || pendingApprovals <= 0) return undefined;
    const owner = visibleDomains.find((domain) => domain.pages.some((page) => page.key === "/approval"));
    return owner ? { [owner.key]: pendingApprovals } : undefined;
  }, [isApprover, pendingApprovals, visibleDomains]);

  const pageBadges = useMemo(
    () => (isApprover && pendingApprovals > 0 ? { "/approval": pendingApprovals } : undefined),
    [isApprover, pendingApprovals],
  );

  // 记忆当前域（供下次进入 domains 形态时恢复）
  useEffect(() => {
    if (mode !== "domains" || !activeDomain) return;
    if (activeDomain.key !== domainKey) {
      setDomainKey(activeDomain.key);
      saveActiveDomain(activeDomain.key);
    }
  }, [mode, activeDomain, domainKey]);

  // 路由变化时关闭移动端抽屉（避免遮挡新页面）
  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  // 全局可观测性：前端错误与 Web Vitals 上报（与旧前端同一后端端点）。
  // 放到**浏览器空闲时**执行：这两件事不影响首屏，但会与首屏渲染抢主线程（错误监控要挂 window.onerror、
  // vitals 要注册 5 个 PerformanceObserver），实测会推迟 FCP。errorMonitor 仍会尽早挂载（首个空闲窗口，
  // 最迟 1s）以保证早期错误不丢。
  useEffect(() => {
    const start = () => {
      initErrorMonitor();
      initWebVitals((report) => {
        apiReportVitals(report).catch((err) => console.error("[vitals] report failed", err));
      });
    };
    const w = window as Window & { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number; cancelIdleCallback?: (id: number) => void };
    if (typeof w.requestIdleCallback === "function") {
      const id = w.requestIdleCallback(start, { timeout: 2000 });
      return () => w.cancelIdleCallback?.(id);
    }
    const timer = window.setTimeout(start, 0);
    return () => window.clearTimeout(timer);
  }, []);

  // 浮层挂载时机：优先空闲回调，最迟 1.2s；无 requestIdleCallback（含 jsdom）时退回 setTimeout
  const [floatingReady, setFloatingReady] = useState(false);
  useEffect(() => {
    const w = window as Window & { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number; cancelIdleCallback?: (id: number) => void };
    if (typeof w.requestIdleCallback === "function") {
      const id = w.requestIdleCallback(() => setFloatingReady(true), { timeout: 1200 });
      return () => w.cancelIdleCallback?.(id);
    }
    const timer = window.setTimeout(() => setFloatingReady(true), 0);
    return () => window.clearTimeout(timer);
  }, []);

  /** 切换业务域：若当前页面不属于该域，跳到该域第一个页面（避免页签高亮与内容错位） */
  const selectDomain = useCallback(
    (key: string) => {
      const domain = visibleDomains.find((item) => item.key === key);
      if (!domain) return;
      setDomainKey(key);
      saveActiveDomain(key);
      if (!domain.pages.some((page) => page.key === activeKey) && domain.pages[0]) {
        navigate(domain.pages[0].key);
      }
    },
    [visibleDomains, activeKey, navigate],
  );

  const openExternal = useCallback((url: string) => {
    // 携带 access_token 便于目标站点（如 HR 独立前端）识别登录态
    const token = safeGetItem("accessToken");
    window.location.href = token ? `${url}?access_token=${encodeURIComponent(token)}` : url;
  }, []);

  const denied = roleNames.length > 0 && !canAccessRoute(roleNames, location.pathname);
  // 形态 class 供验收脚本判定（scripts/verify-frontend.mjs）：
  //   is-domains = 顶部业务域页签 + 二级浮层；is-tabs = 顶部平铺页面页签。两者都不再有侧栏。
  const isDomainsMode = mode === "domains";
  // 移动端：底部导航（高频页面）+「更多」抽屉。窄屏顶栏实测页签区只剩 139px（内容 425px），
  // 必须横滑才能看全，所以把导航下沉到拇指可达区；抽屉在两种形态下都要有。
  const showMobileNav = isMobile && visiblePages.length > 0;

  return (
    <div className={`app-shell${isDomainsMode ? " is-domains" : " is-tabs"}${showMobileNav ? " has-mobile-nav" : ""}`}>
      {showMobileNav ? (
        <Drawer
          open={mobileNavOpen}
          title={mode === "domains" ? activeDomain?.label ?? "导航菜单" : "全部页面"}
          subtitle={mode === "domains" ? "切换业务域或选择页面" : undefined}
          onClose={() => setMobileNavOpen(false)}
          width={288}
        >
          <MobileNavPanel
            mode={mode}
            pages={visiblePages}
            domains={visibleDomains}
            activeDomainKey={activeDomain?.key ?? ""}
            activeKey={activeKey}
            externalPage={HR_PORTAL_PAGE}
            pageBadges={pageBadges}
            onNavigate={(key) => navigate(key)}
            onSelectDomain={selectDomain}
            onOpenExternal={openExternal}
          />
        </Drawer>
      ) : null}

      <div className="app-main">
        <TopNav
          mode={mode}
          pages={visiblePages}
          domains={visibleDomains}
          activeKey={activeKey}
          activeDomainKey={activeDomain?.key ?? ""}
          externalPage={HR_PORTAL_PAGE}
          domainBadges={domainBadges}
          pageBadges={pageBadges}
          isMobile={isMobile}
          userName={user?.displayName || user?.username || "未登录"}
          userRole={roleNames[0] ?? ""}
          onNavigate={(path) => navigate(path)}
          onSelectDomain={selectDomain}
          onOpenMenu={() => setMobileNavOpen(true)}
          onOpenExternal={openExternal}
          onLogout={() => {
            logout();
            navigate("/login", { replace: true });
          }}
        />

        <div className="app-body">
          <main className="app-content">
            <ErrorBoundary>
              {denied ? (
                <div className="app-denied">
                  <div className="app-denied-code">403</div>
                  <div className="app-denied-title">当前角色没有访问该页面的权限</div>
                  <div className="app-denied-desc">
                    页面路径：{location.pathname} · 当前角色：{roleNames.join(" / ") || "未分配"}
                    <br />
                    如需访问，请联系系统管理员在「组织权限」中调整角色。
                  </div>
                  <Button variant="primary" icon="home" onClick={() => navigate("/")}>
                    返回综合驾驶舱
                  </Button>
                </div>
              ) : (
                <div className="page-enter">
                  <ErrorBoundary>
                    <Outlet />
                  </ErrorBoundary>
                </div>
              )}
            </ErrorBoundary>
          </main>
        </div>
      </div>

      {showMobileNav ? <MobileTabBar pages={visiblePages} activeKey={activeKey} onNavigate={(key) => navigate(key)} onOpenMore={() => setMobileNavOpen(true)} /> : null}

      <GlobalSearch />
      {/* 全局浮层：AI 助手（右下）、演示导览（右下）、新手引导（首访遮罩）。
          空闲窗口才挂载：它们的 chunk 与渲染都不参与首屏，先让页面画出来。 */}
      {floatingReady ? (
        <Suspense fallback={null}>
          <AIAssistant />
          <DemoGuide />
          <OnboardingGuide />
        </Suspense>
      ) : null}
      {sessionExpiryModal}
    </div>
  );
}
